import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useSearch } from '@tanstack/react-router'
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Layout } from '@/components/layout'
import { useToast } from '@/lib/hooks/useToast'
import { useHasPermission } from '@/lib/hooks/usePermission'
import { Building2, Search, Plus, RefreshCw, CheckCircle2, XCircle, AlertTriangle } from 'lucide-react'
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table'
import { Badge } from '@/components/ui/badge'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query'
import { tenantService, type Tenant } from '@/lib/services/tenantService'
import { CreateTenantDialog } from './components/CreateTenantDialog'
import { EditTenantDialog } from './components/EditTenantDialog'
import { ViewTenantDialog } from './components/ViewTenantDialog'

/**
 * Tenant Management Admin Page
 *
 * Allows administrators to manage Azure AD tenant whitelists.
 * Only users with 'tenants.manage' permission can create/edit/delete tenants.
 * Users with 'tenants.view' permission can list and view tenant details.
 *
 * Features:
 * - List all whitelisted tenants with pagination
 * - Filter by status (active/inactive) and admin consent status
 * - Search by tenant name or domain
 * - Create new tenant whitelist entries
 * - Edit tenant details
 * - Activate/deactivate tenants
 * - View tenant details and fixed role mapping convention
 * - Track admin consent status
 */
export function TenantManagementPage() {
  const { t } = useTranslation()
  const { toast } = useToast()
  const queryClient = useQueryClient()
  const search = useSearch({ strict: false }) as { tenantId?: string }

  // Permission checks
  const canViewTenants = useHasPermission('tenants.view')
  const canManageTenants = useHasPermission('tenants.manage')

  // State
  const [searchQuery, setSearchQuery] = useState('')
  const [statusFilter, setStatusFilter] = useState<string>('all')
  const [currentPage, setCurrentPage] = useState(1)
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false)
  const [isEditDialogOpen, setIsEditDialogOpen] = useState(false)
  const [isViewDialogOpen, setIsViewDialogOpen] = useState(false)
  const [selectedTenant, setSelectedTenant] = useState<Tenant | null>(null)

  const perPage = 20

  // Fetch tenants
  const {
    data: tenantsData,
    isLoading,
    refetch,
  } = useQuery({
    queryKey: ['tenants', currentPage, perPage, statusFilter, searchQuery],
    queryFn: () =>
      tenantService.list({
        page: currentPage,
        perPage,
        isActive: statusFilter === 'all' ? undefined : statusFilter === 'active',
        search: searchQuery || undefined,
      }),
    enabled: canViewTenants,
  })

  // Mutations
  const activateMutation = useMutation({
    mutationFn: (tenantId: string) => tenantService.activate(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast({
        title: t('admin.tenants.activateSuccess', 'Tenant Activated'),
        description: t(
          'admin.tenants.activateSuccessDescription',
          'Users from this tenant can now sign in.'
        ),
        variant: 'success',
      })
    },
    onError: (error: Error) => {
      toast({
        title: t('admin.tenants.activateError', 'Activation Failed'),
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const deactivateMutation = useMutation({
    mutationFn: (tenantId: string) => tenantService.deactivate(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast({
        title: t('admin.tenants.deactivateSuccess', 'Tenant Deactivated'),
        description: t(
          'admin.tenants.deactivateSuccessDescription',
          'Users from this tenant can no longer sign in.'
        ),
        variant: 'success',
      })
    },
    onError: (error: Error) => {
      toast({
        title: t('admin.tenants.deactivateError', 'Deactivation Failed'),
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const deleteMutation = useMutation({
    mutationFn: (tenantId: string) => tenantService.delete(tenantId),
    onSuccess: () => {
      queryClient.invalidateQueries({ queryKey: ['tenants'] })
      toast({
        title: t('admin.tenants.deleteSuccess', 'Tenant Removed'),
        description: t(
          'admin.tenants.deleteSuccessDescription',
          'Tenant has been removed from whitelist.'
        ),
        variant: 'success',
      })
    },
    onError: (error: Error) => {
      toast({
        title: t('admin.tenants.deleteError', 'Deletion Failed'),
        description: error.message,
        variant: 'destructive',
      })
    },
  })

  const handleToggleStatus = async (tenant: Tenant) => {
    if (tenant.isActive) {
      await deactivateMutation.mutateAsync(tenant.id)
    } else {
      await activateMutation.mutateAsync(tenant.id)
    }
  }

  const handleViewTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setIsViewDialogOpen(true)
  }

  const handleEditTenant = (tenant: Tenant) => {
    setSelectedTenant(tenant)
    setIsEditDialogOpen(true)
  }

  const handleDeleteTenant = async (tenant: Tenant) => {
    if (
      confirm(
        t(
          'admin.tenants.deleteConfirm',
          `Are you sure you want to remove tenant "${tenant.tenantName}" from the whitelist?`
        )
      )
    ) {
      await deleteMutation.mutateAsync(tenant.id)
    }
  }

  const handleClearFilters = () => {
    setSearchQuery('')
    setStatusFilter('all')
  }

  const totalPages = tenantsData ? Math.ceil(tenantsData.total / perPage) : 0

  // Permission check - deny access if no view permission
  if (!canViewTenants) {
    return (
      <Layout>
        <div className="flex items-center justify-center h-full">
          <Card className="w-full max-w-md">
            <CardHeader>
              <CardTitle>{t('admin.tenants.accessDenied', 'Access Denied')}</CardTitle>
            </CardHeader>
            <CardContent>
              <p className="text-muted-foreground">
                {t(
                  'admin.tenants.accessDeniedDescription',
                  'You do not have permission to view tenants.'
                )}
              </p>
            </CardContent>
          </Card>
        </div>
      </Layout>
    )
  }

  // Auto-load and display tenant from URL param
  if (search.tenantId && selectedTenant?.id !== search.tenantId && tenantsData?.items) {
    const tenant = tenantsData.items.find(t => t.id === search.tenantId)
    if (tenant && !isViewDialogOpen) {
      setSelectedTenant(tenant)
      setIsViewDialogOpen(true)
    }
  }

  return (
    <Layout>
      <div className="space-y-6">
        {/* Header */}
        <div>
          <h1 className="text-3xl font-bold flex items-center gap-3">
            <Building2 className="h-8 w-8" />
            {t('admin.tenants.title', 'Tenant Management')}
          </h1>
          <p className="text-muted-foreground mt-2">
            {t(
              'admin.tenants.description',
              'Manage Azure AD tenant whitelists and Entra ID configuration for SSO authentication.'
            )}
          </p>
        </div>

        {/* Filters & Actions */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-col lg:flex-row gap-4">
              {/* Search */}
              <div className="flex-1">
                <div className="relative">
                  <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 h-4 w-4 text-muted-foreground" />
                  <Input
                    placeholder={t(
                      'admin.tenants.searchPlaceholder',
                      'Search by tenant name or domain...'
                    )}
                    value={searchQuery}
                    onChange={e => setSearchQuery(e.target.value)}
                    className="pl-10"
                  />
                </div>
              </div>

              {/* Status Filter */}
              <Select value={statusFilter} onValueChange={setStatusFilter}>
                <SelectTrigger className="w-full lg:w-48">
                  <SelectValue placeholder={t('admin.tenants.filterByStatus', 'Filter by status')} />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="all">{t('admin.tenants.allStatuses', 'All Statuses')}</SelectItem>
                  <SelectItem value="active">{t('admin.tenants.active', 'Active')}</SelectItem>
                  <SelectItem value="inactive">{t('admin.tenants.inactive', 'Inactive')}</SelectItem>
                </SelectContent>
              </Select>

              {/* Action Buttons */}
              <div className="flex gap-2">
                <Button variant="outline" onClick={handleClearFilters}>
                  {t('admin.tenants.clearFilters', 'Clear')}
                </Button>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  {t('admin.tenants.refresh', 'Refresh')}
                </Button>
                {canManageTenants && (
                  <Button onClick={() => setIsCreateDialogOpen(true)}>
                    <Plus className="h-4 w-4 mr-2" />
                    {t('admin.tenants.addTenant', 'Add Tenant')}
                  </Button>
                )}
              </div>
            </div>
          </CardContent>
        </Card>

        {/* Tenants Table */}
        <Card>
          <CardHeader>
            <CardTitle>
              {t('admin.tenants.tenantsListTitle', 'Whitelisted Tenants')} (
              {tenantsData?.total || 0})
            </CardTitle>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <div className="flex items-center justify-center py-12">
                <RefreshCw className="h-8 w-8 animate-spin text-muted-foreground" />
              </div>
            ) : tenantsData && tenantsData.items && tenantsData.items.length > 0 ? (
              <>
                <Table>
                  <TableHeader>
                    <TableRow>
                      <TableHead>{t('admin.tenants.status', 'Status')}</TableHead>
                      <TableHead>{t('admin.tenants.tenantName', 'Tenant Name')}</TableHead>
                      <TableHead>{t('admin.tenants.domain', 'Domain')}</TableHead>
                      <TableHead>{t('admin.tenants.adminConsent', 'Admin Consent')}</TableHead>
                      <TableHead>{t('admin.tenants.lastLogin', 'Last Login')}</TableHead>
                      <TableHead>{t('admin.tenants.addedAt', 'Added At')}</TableHead>
                      <TableHead className="text-right">
                        {t('admin.tenants.actions', 'Actions')}
                      </TableHead>
                    </TableRow>
                  </TableHeader>
                  <TableBody>
                    {tenantsData.items.map(tenant => (
                      <TableRow key={tenant.id} className={tenant.adminConsentGranted ? '' : 'bg-amber-50 dark:bg-amber-950/20'}>
                        <TableCell>
                          {tenant.isActive ? (
                            <Badge variant="default" className="flex items-center gap-1 w-fit">
                              <CheckCircle2 className="h-3 w-3" />
                              {t('admin.tenants.active', 'Active')}
                            </Badge>
                          ) : (
                            <Badge variant="destructive" className="flex items-center gap-1 w-fit">
                              <XCircle className="h-3 w-3" />
                              {t('admin.tenants.inactive', 'Inactive')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="font-medium">
                          <div className="flex items-center gap-2" title={!tenant.adminConsentGranted ? t('admin.tenants.pendingConsentWarning', 'Admin consent pending') : undefined}>
                            {tenant.tenantName}
                            {!tenant.adminConsentGranted && (
                              <AlertTriangle className="h-4 w-4 text-amber-600 dark:text-amber-500" />
                            )}
                          </div>
                        </TableCell>
                        <TableCell className="text-muted-foreground">{tenant.domain}</TableCell>
                        <TableCell>
                          {tenant.adminConsentGranted ? (
                            <Badge variant="default">
                              {t('admin.tenants.granted', 'Granted')}
                            </Badge>
                          ) : (
                            <Badge variant="outline" className="border-amber-500 text-amber-700 dark:text-amber-500">
                              <AlertTriangle className="h-3 w-3 mr-1" />
                              {t('admin.tenants.pending', 'Pending')}
                            </Badge>
                          )}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {tenant.lastLoginAt
                            ? new Date(tenant.lastLoginAt).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell className="text-muted-foreground">
                          {tenant.addedAt
                            ? new Date(tenant.addedAt).toLocaleDateString()
                            : '-'}
                        </TableCell>
                        <TableCell className="text-right">
                          <div className="flex gap-2 justify-end">
                            <Button
                              variant="secondary"
                              size="sm"
                              onClick={() => handleViewTenant(tenant)}
                            >
                              {t('admin.tenants.view', 'View')}
                            </Button>
                            {canManageTenants && (
                              <>
                                <Button
                                  variant="secondary"
                                  size="sm"
                                  onClick={() => handleEditTenant(tenant)}
                                >
                                  {t('admin.tenants.edit', 'Edit')}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleToggleStatus(tenant)}
                                  disabled={
                                    activateMutation.isPending || deactivateMutation.isPending
                                  }
                                >
                                  {tenant.isActive
                                    ? t('admin.tenants.deactivate', 'Deactivate')
                                    : t('admin.tenants.activate', 'Activate')}
                                </Button>
                                <Button
                                  variant="destructive"
                                  size="sm"
                                  onClick={() => handleDeleteTenant(tenant)}
                                  disabled={deleteMutation.isPending}
                                  className="text-destructive hover:text-destructive"
                                >
                                  {t('admin.tenants.delete', 'Delete')}
                                </Button>
                              </>
                            )}
                          </div>
                        </TableCell>
                      </TableRow>
                    ))}
                  </TableBody>
                </Table>

                {/* Pagination */}
                {totalPages > 1 && (
                  <div className="flex items-center justify-between mt-6">
                    <p className="text-sm text-muted-foreground">
                      {t('admin.tenants.showing', 'Showing')} {(currentPage - 1) * perPage + 1} -{' '}
                      {Math.min(currentPage * perPage, tenantsData.total)} {t('admin.tenants.of', 'of')}{' '}
                      {tenantsData.total}
                    </p>
                    <div className="flex gap-2">
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.max(1, p - 1))}
                        disabled={currentPage === 1}
                      >
                        {t('admin.tenants.previous', 'Previous')}
                      </Button>
                      <Button
                        variant="outline"
                        onClick={() => setCurrentPage(p => Math.min(totalPages, p + 1))}
                        disabled={currentPage === totalPages}
                      >
                        {t('admin.tenants.next', 'Next')}
                      </Button>
                    </div>
                  </div>
                )}
              </>
            ) : (
              <div className="text-center py-12">
                <Building2 className="h-12 w-12 mx-auto text-muted-foreground mb-4" />
                <p className="text-muted-foreground">
                  {t('admin.tenants.noTenants', 'No tenants found')}
                </p>
                {canManageTenants && (
                  <Button onClick={() => setIsCreateDialogOpen(true)} className="mt-4">
                    <Plus className="h-4 w-4 mr-2" />
                    {t('admin.tenants.addFirstTenant', 'Add your first tenant')}
                  </Button>
                )}
              </div>
            )}
          </CardContent>
        </Card>
      </div>

      {/* Dialogs */}
      {canManageTenants && (
        <>
          <CreateTenantDialog
            open={isCreateDialogOpen}
            onOpenChange={setIsCreateDialogOpen}
            onSuccess={() => {
              queryClient.invalidateQueries({ queryKey: ['tenants'] })
            }}
          />
          {selectedTenant && (
            <EditTenantDialog
              open={isEditDialogOpen}
              onOpenChange={setIsEditDialogOpen}
              tenant={selectedTenant}
              onSuccess={() => {
                queryClient.invalidateQueries({ queryKey: ['tenants'] })
              }}
            />
          )}
        </>
      )}
      {selectedTenant && (
        <ViewTenantDialog
          open={isViewDialogOpen}
          onOpenChange={setIsViewDialogOpen}
          tenant={selectedTenant}
        />
      )}
    </Layout>
  )
}
